import { CircleAlert, CircleCheck, Eye, SprayCan } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { GRMS_DISPLAY_STATES } from '@bms/common';
import type { GrmsDisplayState, GrmsFrameState, HousekeepingStatus } from '@bms/common';

// ============================================================================
// Palette D — the GRMS colour language
// ----------------------------------------------------------------------------
// Three visual channels carry three separate meanings, with no overlap
// (decision doc §4.1, spec §5.6):
//
//   border hue                → can I sell this room?
//   dashed border + hatch     → the room is disabled, out of sellable inventory
//   tag and stamp colour      → *why* it is disabled
//   header icon chip          → the housekeeping axis
//   red                       → something is wrong; reserved, used nowhere else
//
// Everything is defined exactly twice below — once per frame state, once per
// housekeeping status — and every other lookup in the app is derived from those
// two objects. That includes the summary-bar colours and the housekeeping row
// dot, which used to be hand-maintained copies (decision doc §2.5, §4.5).
// ============================================================================

interface FramePaletteEntry {
  /** Card frame and floor-plan cell border. */
  border: string;
  /** Status tag chip in the card header and drawer header. */
  tag: string;
  /** Floor-plan cell fill and text. */
  cell: string;
  /** Summary-bar badge. */
  summary: { color: string; bgColor: string };
  /**
   * Rotated corner sticker, on the two disabled states only. Its presence is
   * what marks a state as out of sellable inventory: the card blurs its body and
   * footer, keeps the header sharp, and lays the hatch over the frame.
   */
  stamp?: string;
}

/**
 * Keyed by the five states the frame distinguishes, not the seven the summary
 * bar counts — the three housekeeping details share one amber frame and are told
 * apart by the icon chip instead (spec §3.1, §5.6).
 *
 * Green and indigo sit 101° apart, the widest separation available without
 * leaving conventional hues, because `ready` and `occupied` are the two states
 * that fill the board. No status uses red as a frame colour, so the
 * critical-alarm override (`border-destructive border-2 animate-pulse`) is always
 * the only red frame on a floor (decision doc §4.6). In light mode, the
 * stepped status text reads at 5.32–8.23:1 on its 15% card tint (4.84–7.48:1
 * on the page surface), while the amber frame uses amber-700. In dark mode the
 * Occupied text and frame step up to indigo-300 (7.70:1 on its tint, frame
 * 8.92:1 against the card; the previous step read 4.96:1 and 5.74:1, the
 * dimmest of the five).
 */
export const GRMS_FRAME_PALETTE: Record<GrmsFrameState, FramePaletteEntry> = {
  ready: {
    border: 'border-green-600 dark:border-green-400',
    tag: 'bg-green-500/15 text-green-800 dark:text-green-400',
    cell: 'border-green-600 dark:border-green-400 bg-green-50/95 text-green-950 dark:bg-green-950/90 dark:text-green-100',
    summary: { color: 'text-green-800 dark:text-green-400', bgColor: 'bg-green-500/10' },
  },
  occupied: {
    border: 'border-indigo-600 dark:border-indigo-300',
    tag: 'bg-indigo-500/15 text-indigo-800 dark:text-indigo-300',
    cell: 'border-indigo-600 dark:border-indigo-300 bg-indigo-50/95 text-indigo-950 dark:bg-indigo-950/90 dark:text-indigo-100',
    summary: { color: 'text-indigo-800 dark:text-indigo-300', bgColor: 'bg-indigo-500/10' },
  },
  needs_housekeeping: {
    border: 'border-amber-700 dark:border-amber-400',
    tag: 'bg-amber-500/15 text-amber-800 dark:text-amber-400',
    cell: 'border-amber-700 dark:border-amber-400 bg-amber-50/95 text-amber-950 dark:bg-amber-950/90 dark:text-amber-100',
    summary: { color: 'text-amber-800 dark:text-amber-400', bgColor: 'bg-amber-500/10' },
  },
  // The two disabled states share one identical frame: dashed-plus-hatch means
  // "disabled" and nothing else, so out-of-service is no longer separated from a
  // neighbour by a dash alone. The tag and stamp are what tell them apart.
  out_of_order: {
    border: 'border-slate-500 dark:border-slate-400 border-dashed',
    tag: 'bg-red-500/15 text-red-700 dark:text-red-400',
    cell: 'border-slate-500 dark:border-slate-400 border-dashed bg-slate-50/95 text-slate-950 dark:bg-slate-900/90 dark:text-slate-100',
    summary: { color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-500/10' },
    stamp: 'border-red-700 dark:border-red-400 text-red-700 dark:text-red-400',
  },
  out_of_service: {
    border: 'border-slate-500 dark:border-slate-400 border-dashed',
    tag: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
    cell: 'border-slate-500 dark:border-slate-400 border-dashed bg-slate-50/95 text-slate-950 dark:bg-slate-900/90 dark:text-slate-100',
    summary: { color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/15' },
    stamp: 'border-slate-500 dark:border-slate-400 text-slate-500 dark:text-slate-400',
  },
};

/**
 * Diagonal hatch for the two disabled states, laid over the card and the
 * floor-plan cell as a `pointer-events-none` overlay so it composes with the
 * card surface instead of replacing it. Slate at low alpha in both themes —
 * the same neutral as their shared dashed border.
 */
export const GRMS_DISABLED_HATCH =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(100,116,139,0.10)_5px,rgba(100,116,139,0.10)_10px)]'
  + ' dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(148,163,184,0.12)_5px,rgba(148,163,184,0.12)_10px)]';

interface HousekeepingPaletteEntry {
  icon: LucideIcon;
  /** Icon chip in the header badge row, beside DND and MUR. */
  chip: string;
  /** Status dot on the housekeeping page rows. */
  dot: string;
  /** Summary-bar badge and filter chip for this housekeeping value. */
  summary: { color: string; bgColor: string };
}

/**
 * The housekeeping axis, carried by an icon-only chip that reuses the DND/MUR
 * badge box so the second axis costs no meaningful header width (spec §5.6).
 *
 * `ready` deliberately uses emerald rather than the frame's green: a green frame
 * means *sellable* while an emerald chip means *cleaned*, and keeping the two
 * one step apart stops a green-on-green card reading as one fact repeated.
 */
export const GRMS_HOUSEKEEPING_PALETTE: Record<HousekeepingStatus, HousekeepingPaletteEntry> = {
  dirty: {
    icon: CircleAlert,
    chip: 'bg-amber-500/15 text-amber-800 dark:text-amber-400',
    dot: 'bg-amber-500 dark:bg-amber-400',
    summary: { color: 'text-amber-800 dark:text-amber-400', bgColor: 'bg-amber-500/10' },
  },
  cleaning: {
    icon: SprayCan,
    chip: 'bg-violet-500/15 text-violet-800 dark:text-violet-400',
    dot: 'bg-violet-500 dark:bg-violet-400',
    summary: { color: 'text-violet-800 dark:text-violet-400', bgColor: 'bg-violet-500/10' },
  },
  to_inspect: {
    icon: Eye,
    chip: 'bg-cyan-500/15 text-cyan-800 dark:text-cyan-400',
    dot: 'bg-cyan-500 dark:bg-cyan-400',
    summary: { color: 'text-cyan-800 dark:text-cyan-400', bgColor: 'bg-cyan-500/10' },
  },
  ready: {
    icon: CircleCheck,
    chip: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-400',
    dot: 'bg-emerald-500 dark:bg-emerald-400',
    summary: { color: 'text-emerald-800 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' },
  },
};

// ── Derived lookups ────────────────────────────────────────────────────────
// Nothing below defines a colour; each entry points at one of the two objects
// above. Adding a state in one place therefore cannot leave another lookup
// stale, which is the defect the old five hand-kept tables had.

/** The three housekeeping details, as display states. */
const HOUSEKEEPING_DISPLAY_STATES = ['dirty', 'cleaning', 'to_inspect'] as const;

function isHousekeepingDisplayState(state: GrmsDisplayState): state is HousekeepingStatus {
  return (HOUSEKEEPING_DISPLAY_STATES as readonly string[]).includes(state);
}

/**
 * Summary badges and filter chips work in all seven display states: they take
 * the frame colours for the occupancy and service states, and the chip colours
 * for the three housekeeping ones (decision doc §4.5).
 */
export const GRMS_STATUS_SUMMARY_COLORS: Record<GrmsDisplayState, { color: string; bgColor: string }> =
  Object.fromEntries(
    GRMS_DISPLAY_STATES.map((state) => [
      state,
      isHousekeepingDisplayState(state)
        ? GRMS_HOUSEKEEPING_PALETTE[state].summary
        : GRMS_FRAME_PALETTE[state].summary,
    ]),
  ) as Record<GrmsDisplayState, { color: string; bgColor: string }>;

/** Filter-chip tint, following the same seven-value rule as the summary bar. */
export const GRMS_STATUS_FILTER_COLORS: Record<GrmsDisplayState, string> = Object.fromEntries(
  GRMS_DISPLAY_STATES.map((state) => [
    state,
    isHousekeepingDisplayState(state)
      ? GRMS_HOUSEKEEPING_PALETTE[state].chip
      : GRMS_FRAME_PALETTE[state].tag,
  ]),
) as Record<GrmsDisplayState, string>;

/** Shared visual tones for compact status labels and state tags. */
export const GRMS_TAG_TONES = {
  ready: GRMS_FRAME_PALETTE.ready.tag,
  occupied: GRMS_FRAME_PALETTE.occupied.tag,
  housekeeping: GRMS_FRAME_PALETTE.needs_housekeeping.tag,
  outOfOrder: GRMS_FRAME_PALETTE.out_of_order.tag,
  outOfService: GRMS_FRAME_PALETTE.out_of_service.tag,
  dnd: 'bg-red-500/10 text-red-700 dark:text-red-400',
  mur: 'bg-amber-500/10 text-amber-800 dark:text-amber-400',
  warning: 'bg-orange-500/10 text-orange-800 dark:text-orange-400',
  info: 'bg-info/10 text-info-text',
  neutral: 'bg-muted text-muted-foreground',
} as const;

export type GrmsTagTone = keyof typeof GRMS_TAG_TONES;
